import { useState, useEffect } from 'react'
import { motion } from 'motion/react'
import { Button } from '@/components/ui/Button'
import { ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react'
import type { FormData } from '@/hooks/useSubmissionWizard'

interface GuidelinesStepProps {
  formData: FormData
  updateField: <K extends keyof FormData>(field: K, value: FormData[K]) => void
  onNext: () => void
  onBack: () => void
}

const GUIDELINES = [
  'Photos must be your own original work',
  'No AI generated images',
  'No illegal, inappropriate, offensive, or violent content',
  'Photo must be taken during the 2025-2026 school year',
  'Maximum 50 MB per image',
  'By submitting your photo, you grant Sierra Canyon School the right to display and publish the photo in school exhibitions and publications.',
]

export function GuidelinesStep({ updateField, onNext, onBack }: GuidelinesStepProps) {
  const [hasRead, setHasRead] = useState(false)

  // Auto-scroll to top when step loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const handleAccept = () => {
    updateField('guidelinesAcceptedAt', new Date().toISOString())
    onNext()
  }

  return (
    <div className="mx-auto max-w-xl space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
        <h2 className="text-foreground font-display text-3xl font-bold md:text-4xl lg:text-5xl">
          Submission Guidelines
        </h2>
        <p className="text-foreground-muted text-lg">
          Please review and accept the following guidelines before continuing
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-surface border-border space-y-4 rounded-lg border p-6"
      >
        <h3 className="text-foreground text-lg font-semibold">Photo Guidelines</h3>
        <ul className="space-y-3">
          {GUIDELINES.map((guideline, index) => (
            <li key={index} className="text-foreground-muted flex items-start gap-3">
              <CheckCircle className="text-primary mt-0.5 h-5 w-5 flex-shrink-0" />
              <span>{guideline}</span>
            </li>
          ))}
        </ul>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex items-center gap-3"
      >
        <input
          type="checkbox"
          id="accept-guidelines"
          checked={hasRead}
          onChange={(e) => setHasRead(e.target.checked)}
          className="border-border text-primary focus:ring-primary h-5 w-5 rounded focus:ring-offset-0"
        />
        <label htmlFor="accept-guidelines" className="text-foreground cursor-pointer">
          I have read and agree to the submission guidelines
        </label>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="flex items-center gap-4"
      >
        <Button type="button" variant="ghost" size="lg" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Button
          type="button"
          size="lg"
          disabled={!hasRead}
          onClick={handleAccept}
          className="flex-1 md:min-w-[200px] md:flex-none"
        >
          Accept & Continue
          <ArrowRight className="h-5 w-5" />
        </Button>
      </motion.div>
    </div>
  )
}
